import { useQuery } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { getRecentPallets } from '../../api/pallets'
import { db } from '../../outbox/db'
import type { RecentPallet } from '../../api/contracts'
import type { CachedPallet } from '../../outbox/types'

/**
 * Recent pallets, server-first with a device mirror.
 *
 * The mirror is not a cache optimisation — it is what makes the screen usable when the
 * aisle Wi-Fi is gone. A fetch writes through to Dexie; a failed fetch renders whatever
 * Dexie still holds, with no spinner and no empty state.
 */
export function useRecentPallets(): { pallets: RecentPallet[]; isStale: boolean } {
  const query = useQuery({
    queryKey: ['pallets', 'recent'],
    queryFn: async ({ signal }) => {
      const fresh = await getRecentPallets(signal)
      await mirror(fresh)
      return fresh
    },
  })

  const cached = useLiveQuery(() => db.recentPallets.orderBy('cachedAt').reverse().toArray(), [], [])

  if (query.data !== undefined) {
    return { pallets: query.data, isStale: false }
  }

  return { pallets: (cached ?? []).map(fromCache), isStale: true }
}

async function mirror(pallets: RecentPallet[]): Promise<void> {
  const now = Date.now()

  await db.transaction('rw', db.recentPallets, async () => {
    const existing = await db.recentPallets.bulkGet(pallets.map((p) => p.palletCode))
    const bySerialOwner = new Map(
      existing.filter((row): row is CachedPallet => row !== undefined).map((row) => [row.palletCode, row]),
    )

    await db.recentPallets.bulkPut(
      pallets.map<CachedPallet>((pallet) => ({
        palletCode: pallet.palletCode,
        itemCount: pallet.itemCount,
        capacityMax: pallet.capacityMax,
        status: pallet.status,
        // The recent list carries no items, so a detail fetch's list is kept rather
        // than blanked — losing it would leave the pallet screen empty offline.
        serialNumbers: bySerialOwner.get(pallet.palletCode)?.serialNumbers ?? [],
        lastActivityAt: pallet.lastActivityAt,
        cachedAt: now,
      })),
    )
  })
}

function fromCache(cached: CachedPallet): RecentPallet {
  return {
    palletCode: cached.palletCode,
    status: cached.status,
    itemCount: cached.itemCount,
    capacityMax: cached.capacityMax,
    lastActivityAt: cached.lastActivityAt,
  }
}
